import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { bindActionCreators } from 'redux';
import { formatOrderDateToLocalTime, priceFormat } from '../../../util/index';
import { getSelectedOrderFromOrdersSearch } from '../../../extractFromState';
import { STATUS_MAP } from 'constants/rbi';
import ProtectedData from 'components/common/ProtectedData/ProtectedData';
import * as sensitiveData from 'constants/sensitiveDataTypes';
import { OrderDetailsPage, TableTd, OrderTable, Title, TitleImage, TitleRow, TableTr } from '../OrderDetails.styles';

const componentName = 'RbiOrderDetails';

const NA = 'Not Available';

const centsParser = (cents, currency) => {
  cents = parseInt(cents);
  const code = get(currency, 'code', 'USD');

  return typeof cents == 'number' && !isNaN(cents) ? priceFormat(cents / 100, code) : null;
};

const address = (order) => {
  const addressArray = [
    get(order, 'storeAddress.addressLine1'),
    get(order, 'storeAddress.addressLine2'),
    get(order, 'storeAddress.city'),
    get(order, 'storeAddress.state'),
    get(order, 'storeAddress.zip'),
    get(order, 'storeAddress.country'),
  ].filter((value) => value);

  if (addressArray && addressArray.length) {
    return addressArray.map((item) => <div key={item}>{item}</div>);
  }

  return null;
};

const protectData = (order, path, type) => {
  const value = get(order, path);

  return value ? (
    <ProtectedData data={value} dataType={type} component={componentName} customerId={order.userId} />
  ) : (
    NA
  );
};

const renderCards = (card, level = 0, currency) => {
  const root = level === 0;
  if (!card.length && root) {
    return (
      <TableTr>
        <TableTd>Line Items</TableTd>
        <TableTd>{NA}</TableTd>
      </TableTr>
    );
  }

  return card.reduce((acc, orderItem, index) => {
    let styles = {};

    if (!root) {
      styles = { paddingLeft: `${3.5 * level}rem` };
    }

    acc.push(
      <TableTr key={orderItem.sanityId}>
        <TableTd>{root && index === 0 ? 'Items' : ''}</TableTd>
        <TableTd style={styles}>
          {orderItem.quantity} "{orderItem.name}": {centsParser(orderItem.price, currency)}
        </TableTd>
      </TableTr>
    );

    const { children } = orderItem;

    if (children && children.length) {
      return [...acc, ...renderCards(children, level + 1, currency)];
    }

    return acc;
  }, []);
};

const renderRefunds = (refundedItems, currency) => {
  if (refundedItems && refundedItems.length) {
    refundedItems = refundedItems.filter((item) => {
      const refundQuantity = parseInt(item.refundQuantity);
      const refundAmount = parseInt(item.refundAmount);

      return refundQuantity && refundAmount && !isNaN(refundQuantity) && !isNaN(refundAmount);
    });

    return refundedItems.map((item, level) => {
      const root = level === 0;
      const text = `${item.refundQuantity} "${item.name}": ${centsParser(item.refundAmount, currency)}`;

      return (
        <TableTr>
          <TableTd>{root ? 'Refunded Items' : ''}</TableTd>
          <TableTd>{text}</TableTd>
        </TableTr>
      );
    });
  }

  return (
    <TableTr>
      <TableTd>Refunded Items</TableTd>
      <TableTd>{NA}</TableTd>
    </TableTr>
  );
};

const getTotalRefund = (refundedItems, currency) => {
  if (refundedItems && refundedItems.length) {
    const total = refundedItems.reduce((acc, item) => {
      const refund = parseInt(item.refundAmount);

      if (!isNaN(refund)) {
        return acc + refund;
      }

      return acc;
    }, 0);

    if (total) {
      return centsParser(total, currency);
    }
  }

  return null;
};

const RbiOrderDetailsComponent = (props) => {
  const { selectedOrder } = props;

  return (
    <OrderDetailsPage>
      <TitleRow>
        <Title>Order Summary</Title>
        <span>
          <TitleImage alt="" src="/images/green-clipboard.svg" />
        </span>
      </TitleRow>
      <hr />
      <OrderTable>
        <tbody>
          <TableTr>
            <TableTd>Order ID</TableTd>
            <TableTd>{selectedOrder.rbiOrderId || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Order Status</TableTd>
            <TableTd>{STATUS_MAP[selectedOrder.status] || selectedOrder.status || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Created At</TableTd>
            <TableTd>{formatOrderDateToLocalTime(selectedOrder.createdAt, 'custom_rbi')}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Customer Name</TableTd>
            <TableTd>{protectData(selectedOrder, 'userDetails.name', sensitiveData.fullName)}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Customer Email</TableTd>
            <TableTd>{protectData(selectedOrder, 'userDetails.email', sensitiveData.email)}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Customer Phone</TableTd>
            <TableTd>{protectData(selectedOrder, 'userDetails.phoneNumber', sensitiveData.phoneNumber)}</TableTd>
          </TableTr>
          {renderCards(selectedOrder.cart, 0, selectedOrder.currency)}
          {renderRefunds(selectedOrder.refundedItems, selectedOrder.currency)}
          <TableTr>
            <TableTd>Delivery Status</TableTd>
            <TableTd>{get(selectedOrder, 'delivery.status') || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Store Address</TableTd>
            <TableTd>{address(selectedOrder) || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Total</TableTd>
            <TableTd>{centsParser(selectedOrder.totalCents, selectedOrder.currency) || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Tax</TableTd>
            <TableTd>{centsParser(selectedOrder.taxCents, selectedOrder.currency) || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Total Paid</TableTd>
            <TableTd>{centsParser(selectedOrder.totalPaidCents, selectedOrder.currency) || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Tax Paid</TableTd>
            <TableTd>{centsParser(selectedOrder.taxPaidCents, selectedOrder.currency) || NA}</TableTd>
          </TableTr>
          <TableTr>
            <TableTd>Total Refunded</TableTd>
            <TableTd>{getTotalRefund(selectedOrder.refundedItems, selectedOrder.currency) || NA}</TableTd>
          </TableTr>
        </tbody>
      </OrderTable>
    </OrderDetailsPage>
  );
};

RbiOrderDetailsComponent.propTypes = {
  selectedOrder: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrderFromOrdersSearch(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(RbiOrderDetailsComponent);
